import { useEffect, useMemo } from "react";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import { useTheme } from "../../context/ThemeContext";
import {
  getDestinationCoordinates,
  getDestinationPort,
  getEta,
  getExactShipCoordinates,
  getGeoJsonRoutePoints,
  getMapPosition,
  getOriginCoordinates,
  getOriginPort,
  getRouteProgress,
  getTrackingNo,
  getTrackingStatus,
  getVesselName,
  getVoyageNo,
} from "../../utils/shipsGoTracking";

const DEFAULT_CENTER = [32, 18];

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const pointToArray = (point) => {
  if (!point) return null;

  const latitude = Number(point.latitude);
  const longitude = Number(point.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;

  return [latitude, longitude];
};

const pointsAreEqual = (first, second) => {
  if (!first || !second) return false;

  return (
    Math.abs(Number(first[0]) - Number(second[0])) < 0.000001 &&
    Math.abs(Number(first[1]) - Number(second[1])) < 0.000001
  );
};

const removeDuplicatePoints = (points) => {
  return points.filter((point, index, allPoints) => {
    if (!point) return false;

    return !allPoints.some(
      (otherPoint, otherIndex) =>
        otherIndex < index && pointsAreEqual(point, otherPoint),
    );
  });
};

const getResolvedPosition = (shipment) => {
  const status = getTrackingStatus(shipment);
  const destination = getDestinationCoordinates(shipment);
  const origin = getOriginCoordinates(shipment);
  const exact = getExactShipCoordinates(shipment);
  const fallback = getMapPosition(shipment);

  const reachedDestination =
    Boolean(destination) &&
    (status.group === "arrived" || status.group === "delivered");

  const point = reachedDestination
    ? destination
    : exact || fallback || origin || destination;

  return {
    point,
    reachedDestination,
    isExact: Boolean(exact) && !reachedDestination,
  };
};

const getStatusColors = (statusGroup) => {
  if (statusGroup === "delivered") {
    return {
      from: "#059669",
      to: "#10b981",
      shadow: "rgba(16, 185, 129, 0.38)",
    };
  }

  if (statusGroup === "arrived") {
    return {
      from: "#7c3aed",
      to: "#a855f7",
      shadow: "rgba(168, 85, 247, 0.38)",
    };
  }

  return {
    from: "#2563eb",
    to: "#06b6d4",
    shadow: "rgba(37, 99, 235, 0.38)",
  };
};

const createShipmentIcon = ({ statusGroup, selected, reachedDestination }) => {
  const colors = getStatusColors(statusGroup);

  const iconSvg = reachedDestination
    ? `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4.5 7.5L12 3.5L19.5 7.5V16.5L12 20.5L4.5 16.5V7.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
        <path d="M4.8 7.7L12 11.7L19.2 7.7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
        <path d="M12 11.7V20.1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      </svg>
    `
    : `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 15.5L6.1 9H9.1V5.4H14.9V9H17.9L20 15.5L17.6 18.6H6.4L4 15.5Z" fill="currentColor" />
        <path d="M2.8 18.4C3.9 19.3 5 19.7 6.2 19.7C7.4 19.7 8.5 19.3 9.6 18.4C10.7 19.3 11.8 19.7 13 19.7C14.2 19.7 15.3 19.3 16.4 18.4C17.5 19.3 18.6 19.7 19.8 19.7C20.3 19.7 20.7 19.6 21.2 19.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    `;

  return L.divIcon({
    className: "all-shipments-leaflet-icon",
    html: `
      <div
        class="all-shipments-marker ${selected ? "is-selected" : ""}"
        style="--marker-from:${colors.from};--marker-to:${colors.to};--marker-shadow:${colors.shadow};"
      >
        <span class="all-shipments-marker-pulse"></span>
        <span class="all-shipments-marker-core">${iconSvg}</span>
      </div>
    `,
    iconSize: selected ? [56, 56] : [46, 46],
    iconAnchor: selected ? [28, 28] : [23, 23],
    popupAnchor: [0, -24],
  });
};

function FitMapToShipments({ points, selectedPoint }) {
  const map = useMap();

  useEffect(() => {
    if (selectedPoint) {
      map.flyTo(selectedPoint, 6, {
        animate: true,
        duration: 0.8,
      });
      return;
    }

    if (!points.length) return;

    if (points.length === 1) {
      map.flyTo(points[0], 5, {
        animate: true,
        duration: 0.8,
      });
      return;
    }

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds.pad(0.18), {
      animate: true,
      duration: 0.8,
      maxZoom: 6,
    });
  }, [map, points, selectedPoint]);

  return null;
}

export default function AllShipmentsMap({
  shipments = [],
  selectedShipment = null,
  onSelect,
  height = 610,
}) {
  const { theme } = useTheme();

  const mapRows = useMemo(() => {
    return shipments
      .map((shipment) => {
        const resolved = getResolvedPosition(shipment);
        const position = pointToArray(resolved.point);

        if (!position) return null;

        const status = getTrackingStatus(shipment);

        return {
          shipment,
          position,
          status,
          reachedDestination: resolved.reachedDestination,
          isExact: resolved.isExact,
        };
      })
      .filter(Boolean);
  }, [shipments]);

  const selectedRow = useMemo(() => {
    if (!selectedShipment) return null;

    return (
      mapRows.find((row) => row.shipment.id === selectedShipment.id) || null
    );
  }, [mapRows, selectedShipment]);

  const selectedRoutePoints = useMemo(() => {
    if (!selectedShipment || !selectedRow) return [];

    const geoJsonPoints = getGeoJsonRoutePoints(selectedShipment);

    if (Array.isArray(geoJsonPoints) && geoJsonPoints.length >= 2) {
      return removeDuplicatePoints(geoJsonPoints);
    }

    return removeDuplicatePoints([
      pointToArray(getOriginCoordinates(selectedShipment)),
      selectedRow.position,
      pointToArray(getDestinationCoordinates(selectedShipment)),
    ]);
  }, [selectedRow, selectedShipment]);

  const allPositions = useMemo(
    () => mapRows.map((row) => row.position),
    [mapRows],
  );

  if (!mapRows.length) {
    return (
      <div
        className="grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center dark:border-slate-700 dark:bg-slate-950"
        style={{ height }}
      >
        <div>
          <p className="font-semibold text-slate-900 dark:text-slate-100">
            No map positions available
          </p>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
            The current tracking records do not yet contain valid ShipsGo or
            port coordinates.
          </p>
        </div>
      </div>
    );
  }

  const tileUrl =
    theme === "dark"
      ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
      : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  const tileAttribution =
    theme === "dark"
      ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
      : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <style>{`
        .all-shipments-leaflet-icon {
          background: transparent;
          border: 0;
        }

        .all-shipments-marker {
          position: relative;
          width: 46px;
          height: 46px;
          display: grid;
          place-items: center;
          transition: transform 180ms ease;
        }

        .all-shipments-marker.is-selected {
          width: 56px;
          height: 56px;
          transform: scale(1.04);
          z-index: 20;
        }

        .all-shipments-marker-pulse {
          position: absolute;
          inset: 5px;
          border-radius: 999px;
          background: var(--marker-to);
          opacity: 0.28;
          animation: all-shipments-pulse 2s ease-out infinite;
        }

        .all-shipments-marker-core {
          position: relative;
          width: 36px;
          height: 36px;
          display: grid;
          place-items: center;
          border: 3px solid #ffffff;
          border-radius: 999px;
          color: #ffffff;
          background: linear-gradient(135deg, var(--marker-from), var(--marker-to));
          box-shadow: 0 10px 24px var(--marker-shadow);
        }

        .all-shipments-marker.is-selected .all-shipments-marker-core {
          width: 44px;
          height: 44px;
          box-shadow: 0 12px 30px var(--marker-shadow), 0 0 0 4px rgba(255,255,255,0.35);
        }

        .all-shipments-marker-core svg {
          width: 18px;
          height: 18px;
        }

        .all-shipments-marker.is-selected .all-shipments-marker-core svg {
          width: 21px;
          height: 21px;
        }

        @keyframes all-shipments-pulse {
          0% { transform: scale(0.7); opacity: 0.42; }
          100% { transform: scale(1.65); opacity: 0; }
        }
      `}</style>

      <MapContainer
        center={selectedRow?.position || allPositions[0] || DEFAULT_CENTER}
        zoom={4}
        scrollWheelZoom
        style={{ height, width: "100%" }}
      >
        <TileLayer url={tileUrl} attribution={tileAttribution} />

        <FitMapToShipments
          points={allPositions}
          selectedPoint={selectedRow?.position || null}
        />

        {selectedRoutePoints.length >= 2 ? (
          <Polyline
            positions={selectedRoutePoints}
            pathOptions={{
              color:
                selectedRow?.status.group === "delivered"
                  ? "#10b981"
                  : selectedRow?.status.group === "arrived"
                    ? "#8b5cf6"
                    : "#2563eb",
              weight: 4,
              opacity: 0.8,
              dashArray: "10 9",
            }}
          />
        ) : null}

        {mapRows.map((row) => {
          const isSelected = row.shipment.id === selectedShipment?.id;
          const progress = getRouteProgress(row.shipment);

          return (
            <Marker
              key={row.shipment.id}
              position={row.position}
              icon={createShipmentIcon({
                statusGroup: row.status.group,
                selected: isSelected,
                reachedDestination: row.reachedDestination,
              })}
              eventHandlers={{
                click: () => onSelect?.(row.shipment),
              }}
              zIndexOffset={isSelected ? 1000 : 0}
            >
              <Popup minWidth={245}>
                <div style={{ lineHeight: 1.55 }}>
                  <strong>{getTrackingNo(row.shipment)}</strong>
                  <br />
                  Status: {row.status.label}
                  <br />
                  Vessel: {getVesselName(row.shipment)}
                  <br />
                  Voyage: {getVoyageNo(row.shipment)}
                  <br />
                  Route: {getOriginPort(row.shipment)} →{" "}
                  {getDestinationPort(row.shipment)}
                  <br />
                  ETA: {formatDate(getEta(row.shipment))}
                  <br />
                  Progress: {progress}%
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={() => onSelect?.(row.shipment)}
                      style={{
                        width: "100%",
                        border: 0,
                        borderRadius: 8,
                        padding: "8px 10px",
                        background: "#2563eb",
                        color: "#ffffff",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      View shipment
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      <div className="pointer-events-none absolute bottom-4 right-4 z-[500] rounded-2xl border border-slate-200 bg-white/95 p-3 text-xs shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
        <p className="font-bold text-slate-900 dark:text-slate-100">Status</p>
        <div className="mt-2 space-y-2 text-slate-600 dark:text-slate-300">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
            In transit
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-violet-500" />
            Arrived at port
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
            Delivered
          </div>
        </div>
      </div>
    </div>
  );
}
